import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

const CONTACT_ENDPOINT =
  'https://forms.2merative.com/api/2merative-contact.php?form=contact'

// Services offered on the form. `id` matches the service keys in
// data/services.js so CTA links can pre-select them with
// /contact?service=<id> (comma-separated for several) or ?intent=chatbot.
// `value` is the text emailed to us.
const SERVICE_OPTIONS = [
  { id: 'website-development', value: 'Website design & development', aliases: ['website', 'web-design'] },
  { id: 'seo', value: 'SEO & GEO (search and AI visibility)', aliases: ['seo-geo', 'geo'] },
  { id: 'google-ads', value: 'Google Ads', aliases: ['ads'] },
  { id: 'chatbot', value: 'AI chatbot', aliases: ['ai-chatbot', 'chatbots'] },
  { id: 'business-integrations', value: 'Business integrations & automation', aliases: ['integrations', 'automation'] },
  { id: 'digital-support', value: 'Ongoing support', aliases: ['support'] },
  { id: 'digital-strategy', value: 'Digital strategy', aliases: ['strategy'] },
  { id: 'unsure', value: 'Not sure yet', aliases: [] },
]

// Services quoted as a one-off project vs. ongoing monthly work.
const PROJECT_SERVICES = ['website-development', 'chatbot', 'business-integrations', 'digital-strategy', 'unsure']
const MONTHLY_SERVICES = ['seo', 'google-ads', 'digital-support']

function servicesFromParams(searchParams) {
  const requested = [
    ...(searchParams.get('service') || '').split(','),
    searchParams.get('intent') || '',
  ]
    .map(value => value.trim().toLowerCase())
    .filter(Boolean)

  return SERVICE_OPTIONS.filter(
    option => requested.includes(option.id) || option.aliases.some(alias => requested.includes(alias))
  ).map(option => option.id)
}

function formDataToPayload(formData) {
  const payload = {}

  for (const [key, value] of formData.entries()) {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      if (!Array.isArray(payload[key])) {
        payload[key] = [payload[key]]
      }

      payload[key].push(value)
    } else {
      payload[key] = value
    }
  }

  return payload
}

const fieldClass =
  'w-full rounded-xl border border-gray-300 bg-white p-3 text-base text-brand-text focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30'
const labelClass = 'mb-1.5 block font-semibold text-brand-heading'

// Compact selectable chips (radio or checkbox) that wrap on small screens.
function ChoiceChips({ name, label, hint, options, type = 'radio' }) {
  return (
    <fieldset>
      <legend className={labelClass}>{label}</legend>
      {hint && <p className="-mt-0.5 mb-2 text-sm text-brand-textSoft">{hint}</p>}

      <div className="mt-2 flex flex-wrap gap-2">
        {options.map(option => (
          <label key={option} className="cursor-pointer">
            <input type={type} name={name} value={option} className="peer sr-only" />
            <span className="inline-flex min-h-[44px] items-center rounded-full border border-gray-300 px-4 py-2 text-sm text-brand-text transition hover:border-brand-primary peer-checked:border-brand-primary peer-checked:bg-brand-primary peer-checked:text-white peer-focus-visible:ring-2 peer-focus-visible:ring-brand-primary peer-focus-visible:ring-offset-2">
              {option}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}

function TextField({ id, name, label, required = false, ...props }) {
  return (
    <div>
      <label htmlFor={id} className={labelClass}>
        {label}
        {required ? ' *' : ''}
      </label>
      <input id={id} name={name} required={required} className={fieldClass} {...props} />
    </div>
  )
}

// A group of follow-up questions shown only for a selected service.
function ServiceQuestions({ title, children }) {
  return (
    <section className="space-y-5 rounded-xl2 border border-brand-border bg-brand-bg p-5 sm:p-6">
      <h3 className="text-sm font-semibold uppercase tracking-[0.12em] text-brand-primary">{title}</h3>
      {children}
    </section>
  )
}

export default function ContactForm() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const intent = searchParams.get('intent') || ''

  const [services, setServices] = useState(() => servicesFromParams(searchParams))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [servicesError, setServicesError] = useState(false)

  // Follow a new CTA link while already on /contact.
  useEffect(() => {
    setServices(servicesFromParams(searchParams))
  }, [searchParams])

  const has = (...ids) => ids.some(id => services.includes(id))

  const toggleService = id => {
    setServicesError(false)
    setServices(current =>
      current.includes(id) ? current.filter(item => item !== id) : [...current, id]
    )
  }

  const handleSubmit = async event => {
    event.preventDefault()

    if (submitting) return

    const form = event.currentTarget
    const formData = new FormData(form)

    if (formData.get('_gotcha')) {
      navigate('/thank-you')
      return
    }

    if (services.length === 0) {
      setServicesError(true)
      form.querySelector('input[name="services"]')?.focus()
      return
    }

    setSubmitting(true)
    setError('')

    const payload = formDataToPayload(formData)

    delete payload._gotcha

    // `projectType` is an existing field ("What do you need help with?"), so the
    // selected services always arrive in a field the handler already emails.
    payload.services = formData.getAll('services')
    payload.projectType = payload.services.join(', ')
    payload.source = '2merative.com website contact form'
    payload.submittedAt = new Date().toISOString()

    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      const result = await response.json().catch(() => null)

      if (!response.ok || result?.success !== true) {
        throw new Error(
          result?.error || 'Form submission failed.'
        )
      }

      form.reset()
      navigate('/thank-you')
    } catch (submissionError) {
      console.error(
        '2merative contact form error:',
        submissionError
      )

      setError(
        submissionError instanceof Error
          ? submissionError.message
          : 'An error occurred. Please try again later.'
      )

      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mx-auto mb-10 mt-10 max-w-3xl space-y-8 rounded-xl2 border border-brand-border bg-white px-5 pb-8 pt-7 text-left shadow-card sm:px-8"
    >
      <div className="text-center">
        <h2 className="text-2xl font-bold tracking-tight text-brand-heading">
          Tell us about your project
        </h2>
        <p className="mx-auto mt-2 max-w-2xl text-brand-textSoft">
          Choose the services you are interested in and answer a few short questions. We will be
          in touch to discuss next steps.
        </p>
      </div>

      <div className="hidden" aria-hidden="true">
        <label>
          Do not fill this out

          <input
            name="_gotcha"
            tabIndex={-1}
            autoComplete="off"
          />
        </label>
      </div>

      {intent && <input type="hidden" name="enquiryIntent" value={intent} />}

      <div className="grid gap-5 sm:grid-cols-2">
        <TextField id="contact-name" name="name" label="Name" required autoComplete="name" />
        <TextField id="business-name" name="businessName" label="Business name" required autoComplete="organization" />
        <TextField id="contact-email" name="email" type="email" label="Email" required autoComplete="email" />
        <TextField id="contact-phone" name="phone" type="tel" label="Phone" required autoComplete="tel" />
        <TextField id="current-website" name="currentWebsite" type="url" label="Current website (if any)" placeholder="https://" />
        <TextField id="business-type" name="businessType" label="Type of business" placeholder="For example: builder, law firm, clinic" />
      </div>

      <fieldset aria-describedby={servicesError ? 'services-error' : undefined}>
        <legend className={labelClass}>Which services are you interested in? *</legend>
        <p className="-mt-0.5 text-sm text-brand-textSoft">Select all that apply.</p>

        <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
          {SERVICE_OPTIONS.map(option => (
            <label key={option.id} className="cursor-pointer">
              <input
                type="checkbox"
                name="services"
                value={option.value}
                checked={services.includes(option.id)}
                onChange={() => toggleService(option.id)}
                className="peer sr-only"
              />
              <span className="flex min-h-[52px] items-center gap-3 rounded-xl border border-gray-300 px-4 py-3 text-brand-text transition hover:border-brand-primary peer-checked:border-brand-primary peer-checked:bg-brand-primarySoft peer-checked:font-semibold peer-checked:text-brand-heading peer-focus-visible:ring-2 peer-focus-visible:ring-brand-primary peer-focus-visible:ring-offset-2">
                <span
                  aria-hidden="true"
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border text-xs ${
                    services.includes(option.id)
                      ? 'border-brand-primary bg-brand-primary text-white'
                      : 'border-gray-400 bg-white text-transparent'
                  }`}
                >
                  ✓
                </span>
                {option.value}
              </span>
            </label>
          ))}
        </div>

        {servicesError && (
          <p id="services-error" role="alert" className="mt-2 text-sm text-red-600">
            Please select at least one service, or choose “Not sure yet”.
          </p>
        )}
      </fieldset>

      {has('website-development') && (
        <ServiceQuestions title="Website">
          <ChoiceChips
            name="websiteStage"
            label="Where are you starting from?"
            options={['A new website', 'Rebuilding an existing website', 'Improving my current website']}
          />
          <ChoiceChips
            name="websiteGoal"
            label="What is the main goal of the website?"
            options={[
              'Get more enquiries',
              'Look more professional',
              'Make it easier for customers to contact us',
              'Replace an old or poor website',
              'Support bookings or appointments',
              'Sell products online',
              'Other',
            ]}
          />
          <ChoiceChips
            name="features"
            type="checkbox"
            label="Which features do you need?"
            options={['Contact form', 'Online booking', 'Online shop', 'Blog or articles', 'Portfolio or gallery', 'Events or fixtures']}
          />
          <div className="grid gap-5 sm:grid-cols-2">
            <ChoiceChips name="haveLogo" label="Do you have branding or a logo?" options={['Yes', 'No', 'Some, but it needs work']} />
            <ChoiceChips
              name="hostingHelp"
              label="Need help with domain or hosting?"
              options={['Yes, I need help with both', 'I need help with hosting only', 'I need help with domain only', 'No, I already have them sorted', 'Not sure']}
            />
          </div>
        </ServiceQuestions>
      )}

      {has('seo') && (
        <ServiceQuestions title="SEO & GEO">
          <ChoiceChips
            name="seoFocus"
            type="checkbox"
            label="What would you most like to improve?"
            options={[
              'Local search and Google Business Profile',
              'Visibility for key services',
              'How AI-powered search describes us',
              'Technical issues or a drop in traffic',
              'Not sure',
            ]}
          />
        </ServiceQuestions>
      )}

      {has('google-ads') && (
        <ServiceQuestions title="Google Ads">
          <ChoiceChips
            name="adsStatus"
            label="Are you advertising on Google now?"
            options={['Not yet', 'Yes, and I want better results', 'Ads are paused']}
          />
          <ChoiceChips
            name="adSpend"
            label="Expected monthly ad spend"
            hint="The amount paid to Google, separate from management fees."
            options={['Under $1,000', '$1,000 to $3,000', '$3,000 to $10,000', '$10,000+', 'Not sure yet']}
          />
        </ServiceQuestions>
      )}

      {has('chatbot') && (
        <ServiceQuestions title="AI chatbot">
          <ChoiceChips
            name="chatbotGoals"
            type="checkbox"
            label="What should the chatbot help with?"
            options={[
              'Answer common questions',
              'Guide visitors to the right service',
              'Capture and qualify leads',
              'Direct people to bookings or forms',
              'Help outside business hours',
              'Hand over to our team',
            ]}
          />
        </ServiceQuestions>
      )}

      {has('business-integrations', 'chatbot') && (
        <div>
          <label htmlFor="process-improvement" className={labelClass}>
            {has('business-integrations')
              ? 'Which systems do you use, and what manual tasks would you like to reduce?'
              : 'Which systems should the chatbot work with, and what do customers ask most?'}
          </label>
          <p className="mb-2 text-sm text-brand-textSoft">
            For example: your CRM, booking or e-commerce platform, double handling of enquiries,
            or questions your team answers every day.
          </p>
          <textarea id="process-improvement" name="processImprovement" rows={4} className={fieldClass} />
        </div>
      )}

      {has('digital-support') && (
        <ServiceQuestions title="Ongoing support">
          <ChoiceChips
            name="supportNeeds"
            type="checkbox"
            label="What ongoing help do you need?"
            options={['Website updates', 'Technical support', 'SEO & GEO monitoring', 'Google Ads management', 'Health checks and reporting']}
          />
        </ServiceQuestions>
      )}

      {services.length > 0 && (
        <div className="space-y-6">
          {has(...PROJECT_SERVICES) && (
            <ChoiceChips
              name="budget"
              label="Approximate project budget"
              options={['Under $3,000', '$3,000 to $7,000', '$7,000 to $15,000', '$15,000+', 'Not sure yet']}
            />
          )}
          {has(...MONTHLY_SERVICES) && (
            <ChoiceChips
              name="monthlyInvestment"
              label="Approximate monthly investment for ongoing work"
              hint={has('google-ads') ? 'Excluding Google Ads spend.' : undefined}
              options={['Under $500', '$500 to $1,500', '$1,500 to $3,000', '$3,000+', 'Not sure yet']}
            />
          )}
          <ChoiceChips
            name="timeline"
            label="What timeline are you working to?"
            options={['As soon as possible', 'Within 1 month', 'Within 2 to 3 months', 'No fixed timeline yet']}
          />
        </div>
      )}

      <div>
        <label htmlFor="extra-notes" className={labelClass}>
          Anything else we should know?
        </label>
        <textarea
          id="extra-notes"
          name="extraNotes"
          rows={5}
          placeholder="What is not working, what you would like to achieve, or anything about your business situation."
          className={fieldClass}
        />
      </div>

      {error && (
        <p
          className="text-center text-sm text-red-600"
          role="alert"
        >
          {error}
        </p>
      )}

      <div className="text-center">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex min-h-[48px] items-center justify-center rounded-full bg-brand-primary px-8 py-3 font-semibold text-white shadow-button transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Sending...' : 'Send Enquiry'}
        </button>
      </div>
    </form>
  )
}
